"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">{text}</span>;
}
function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}
function CheckIcon({ size = "sm" }) {
  return (
    <div className={`bg-blue-700 flex-shrink-0 flex items-center justify-center ${size === "sm" ? "w-5 h-5 mt-0.5" : "w-4 h-4"}`}>
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}
function CrossIcon() {
  return (
    <div className="w-4 h-4 bg-slate-200 flex-shrink-0 flex items-center justify-center">
      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
        <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    </div>
  );
}

/* ─── Data ────────────────────────────────────────────────── */
const PLAN_FEATURES = [
  { label: "Best For",       shared: "Blogs & Small Sites",  vps: "Growing Businesses",  dedicated: "High-Traffic Apps",  cloud: "Dynamic Scale Needs" },
  { label: "Storage",        shared: "100 GB SSD",           vps: "100 GB SSD",          dedicated: "1–2 TB SSD",         cloud: "Auto-Scaling" },
  { label: "RAM",            shared: "Shared",               vps: "2–8 GB",              dedicated: "16–64 GB",           cloud: "On Demand" },
  { label: "CPU",            shared: "Shared",               vps: "2–4 Cores",           dedicated: "8–16 Cores",         cloud: "On Demand" },
  { label: "Uptime SLA",     shared: "99.9%",                vps: "99.9%",               dedicated: "99.9%",              cloud: "99.99%" },
  { label: "Free SSL",       shared: true,                   vps: true,                  dedicated: true,                 cloud: true },
  { label: "Daily Backup",   shared: true,                   vps: true,                  dedicated: true,                 cloud: true },
  { label: "Root Access",    shared: false,                  vps: true,                  dedicated: true,                 cloud: true },
  { label: "Support",        shared: "24/7 Chat",            vps: "24/7 Priority",       dedicated: "24/7 Dedicated",     cloud: "24/7 Dedicated" },
];

const PLANS = ["Shared", "VPS", "Dedicated", "Cloud"];

const TECH_STACK = [
  { category: "Web Servers", items: ["LiteSpeed", "NGINX", "Apache", "HTTP/3 support"] },
  { category: "Security",    items: ["CloudLinux", "Imunify360", "DDoS mitigation", "WAF", "Malware scanning"] },
  { category: "Caching",     items: ["LiteSpeed Cache", "Varnish", "Redis", "OPcache"] },
  { category: "Control Panel",items: ["cPanel", "Plesk", "Custom dashboard"] },
  { category: "Databases",   items: ["MySQL 8.0", "PostgreSQL 15", "Redis", "MariaDB"] },
  { category: "CDN",         items: ["Cloudflare integration for global content delivery"] },
];

const FEATURES = [
  { title: "99.9% Uptime SLA",          body: "Guaranteed uptime with 24/7 monitoring, automatic failover and real-time incident response." },
  { title: "Free SSL on Every Plan",     body: "All hosting plans include free SSL certificates for secure HTTPS — automatically renewed." },
  { title: "Daily Automated Backups",    body: "Every plan includes automated daily backups with one-click restore — no data loss, ever." },
  { title: "SSD-Only Storage",           body: "All servers run on enterprise SSD storage — 10x faster read/write than traditional HDD hosting." },
  { title: "Free Migration",             body: "We migrate your existing website to Farsight hosting at no cost, with zero downtime." },
  { title: "24/7 Expert Support",        body: "Round-the-clock technical support from hosting specialists — chat, ticket and phone." },
];

const FAQS = [
  { q: "What is managed hosting?",           a: "Managed hosting means we handle server setup, security patching, updates, performance tuning, backups and monitoring. You focus on your business — we handle the infrastructure." },
  { q: "Which hosting plan should I choose?",a: "Shared hosting is ideal for small websites and blogs. VPS is best for growing businesses needing more control. Dedicated servers suit high-traffic applications. Cloud hosting is best for unpredictable traffic and auto-scaling needs. We advise during the free consultation." },
  { q: "What uptime do you guarantee?",      a: "We guarantee 99.9% uptime on Shared, VPS and Dedicated plans and 99.99% on Cloud hosting — backed by automated failover, redundant infrastructure and SLA compensation if breached." },
  { q: "Do you provide free SSL certificates?",a: "Yes. Free SSL certificates (HTTPS) are included with every hosting plan and auto-renewed — so your website is always secure without any manual work." },
  { q: "Can you migrate my existing website?",a: "Yes. We provide free website migration from your current host with zero downtime. Our team handles the entire process including DNS cutover, file transfer and database migration." },
  { q: "How quickly can I get started?",     a: "Shared and VPS plans are provisioned within minutes of sign-up. Dedicated servers are typically ready within 24–48 hours. Cloud environments are available on demand." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Cell renderer ───────────────────────────────────────── */
function Cell({ val }: { val: string | boolean }) {
  if (val === true)  return <div className="flex justify-center"><CheckIcon size="xs" /></div>;
  if (val === false) return <div className="flex justify-center"><CrossIcon /></div>;
  return <span className="text-[13px] text-slate-600 leading-snug">{val}</span>;
}

type PlanFeature = typeof PLAN_FEATURES[number];

function PlanFeatureRow({ row, index }: { row: PlanFeature; index: number }) {
  const [ref, v] = useVisible<HTMLTableRowElement>();
  return (
    <tr
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100" : "opacity-0"} ${index % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
      style={{ transitionDelay: `${(index % 5) * 0.06}s` }}
    >
      <td className="px-5 py-4 text-[13px] font-bold text-slate-700">{row.label}</td>
      <td className="px-5 py-4 text-center"><Cell val={row.shared} /></td>
      <td className="px-5 py-4 text-center"><Cell val={row.vps} /></td>
      <td className="px-5 py-4 text-center"><Cell val={row.dedicated} /></td>
      <td className="px-5 py-4 text-center bg-blue-50"><Cell val={row.cloud} /></td>
    </tr>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function HostingPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>Web Hosting</span>
          </nav>

          {/* Badge tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["Shared", "VPS", "Dedicated", "Cloud Hosting", "Free Migration"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Premium Hosting" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Fast, Secure, Reliable Hosting — Built for Business
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Enterprise-grade web hosting with 99.9% uptime, SSD storage, free SSL, daily backups and 24/7 expert support — at every budget level.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              VIEW HOSTING PLANS
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              GET FREE MIGRATION
            </button>
          </div>
        </div>

        {/* Right: photo */}
        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/cloud.png"
            alt="Web hosting infrastructure"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "99.9%",  l: "Uptime SLA" },
          { v: "4",      l: "Hosting Plans" },
          { v: "Free",   l: "SSL + Migration" },
          { v: "24/7",   l: "Expert Support" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ HOSTING PLANS — comparison table ═══════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-8 lg:mb-12">
              <Eyebrow text="Hosting Plans" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Four Plans. Every Business Size.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From personal blogs to high-traffic enterprise applications — we have the right plan for you.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="bg-slate-800 text-left px-5 py-4 text-[11px] font-bold text-white tracking-[0.12em] uppercase w-[22%]">
                      Feature
                    </th>
                    {PLANS.map((plan, i) => (
                      <th
                        key={plan}
                        className={`px-5 py-4 text-center text-[12px] font-bold tracking-[0.08em] uppercase ${i === 3 ? "bg-blue-700 text-white" : "bg-slate-700 text-white"}`}
                      >
                        {plan}
                        {i === 3 && (
                          <span className="block text-[10px] font-medium text-blue-200 tracking-[0.1em] normal-case mt-0.5">Most Scalable</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PLAN_FEATURES.map((row, ri) => (
                    <PlanFeatureRow key={ri} row={row} index={ri} />
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ KEY FEATURES — 2×3 grid ════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="What&apos;s Included on Every Plan" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Enterprise Features at Every Level
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              Every Farsight hosting plan ships with the features that matter most — no hidden costs, no paywalled essentials.
            </p>
            <div className="flex flex-col gap-0">
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 py-5 ${i < FEATURES.length - 1 ? "border-b border-slate-100" : ""}`}
                >
                  <CheckIcon size="sm" />
                  <div>
                    <p className="text-[14px] font-bold text-slate-900 mb-1">{f.title}</p>
                    <p className="text-[13.5px] text-slate-500 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=700&q=80"
                alt="Hosting features"
                className="w-full h-[280px] sm:h-[380px] lg:h-[480px] object-cover"
              />
              {/* Floating stat */}
              <div className="absolute bottom-[-24px] right-[-24px] bg-white border border-slate-200 shadow-xl px-6 py-5 hidden lg:block">
                <p className="text-[30px] font-black text-blue-700 leading-none">99.9%</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Uptime Guaranteed</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">Free</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">SSL + Migration</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ TECH STACK ═════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80"
              alt="Hosting technology stack"
              className="w-full h-[240px] sm:h-[340px] lg:h-[420px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="Hosting Technology Stack" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Enterprise-Grade Infrastructure
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Built on the same technology stack used by the world&apos;s largest hosting providers — optimised for speed, security and reliability.
            </p>

            <div className="flex flex-col gap-0.5">
              {TECH_STACK.map((row, i) => (
                <Reveal key={i} from="left" delay={i * 0.08}>
                  <div className="bg-white border border-slate-200 px-5 py-4">
                    <div className="flex items-start gap-4">
                      <div className="w-1.5 h-1.5 bg-blue-700 flex-shrink-0 mt-2" />
                      <div>
                        <p className="text-[12px] font-bold text-blue-700 tracking-[0.08em] uppercase mb-1.5">{row.category}</p>
                        <p className="text-[13px] text-slate-500 leading-relaxed">{row.items.join(" · ")}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Web Hosting
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Everything you need to know before choosing a hosting plan with Farsight Solutions.
            </p>
            <img
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=700&q=80"
              alt="Hosting FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Take Your Website Online Today" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Reliable Hosting with Exceptional Performance
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Choose a plan, migrate your existing website for free and go live — backed by 99.9% uptime and 24/7 expert support.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Not sure which plan is right for you? Our team will assess your traffic, storage and performance needs and recommend the best fit — at no cost.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                VIEW HOSTING PLANS
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                GET FREE MIGRATION
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}